import React, { useEffect, useState } from 'react';
import { CommentType } from '../../models/commentType';
import { FullProperty } from '../../models/fullProperty';
import { CommentsClient } from '../../services/functions/commentsClient';
import { logEvent } from '../../firebase';
import { MetricsNames } from '../../services/firebase/metricsService';
import CommentsSection from '../../listingDetailsPage/ratings/comments';
import { ProfileType } from '../../models/profileType';
import { StringsService } from '../../services/strings/strings';
import { Box } from '@mui/system';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Rating, Typography } from '@mui/material';
import AsyncString from '../../shared/asyncString';

export const CommentsPage: React.FC<{ restaurant: FullProperty, profile: ProfileType }> = ({ restaurant, profile }) => {

    const [restaurantName, setRestaurantName] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const [comments, setComments] = useState<CommentType[]>([]);
    const [rating, setRating] = useState<number>(0);
    const [numRatings, setNumRatings] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);


    useEffect(() => {
        const fetchComments = async () => {
            setIsLoading(true)
            setRestaurantName(await StringsService.getStringById(`${restaurant.id}-restaurant-name`));
            const { comments: _comments, rating: _rating, numRatings: _numRatings } = await CommentsClient.getComments(restaurant.id)
            setRating(_rating)
            setComments(_comments)
            setNumRatings(_numRatings)
            setIsLoading(false)
        }
        logEvent(MetricsNames.VIEW_ITEM);
        fetchComments();
    }, [restaurant]);

    const addCommentCall = async (data: { parentId?: string; text: string; rating: number }) => {
        setIsLoading(true)
        try {
            await CommentsClient.addComment({
                restaurantId: restaurant.id,
                authorId: profile.id,
                author: restaurantName,
                text: data.text,
                rating: data.rating,
                parentId: data.parentId
            });
            const { comments: _comments, rating: _rating, numRatings: _numRatings } = await CommentsClient.getComments(restaurant.id)
            setRating(_rating)
            setComments(_comments)
            setNumRatings(_numRatings)
        } catch (error) {
            setError((error as Error).message)
        }

        setIsLoading(false)
    }

    const flagCommentAsInappropriate = async (commentId: string, parentId?: string) => {
        setIsLoading(true)
        console.log("Flagging comment as inappropriate:", {
            restaurantId: restaurant.id,
            commentId,
            parentId
        });

        try {
            await CommentsClient.flagCommentAsInappropriate({
                restaurantId: restaurant.id,
                commentId
            })
            const { comments: _comments, rating: _rating, numRatings: _numRatings } = await CommentsClient.getComments(restaurant.id)
            setRating(_rating)
            setComments(_comments)
            setNumRatings(_numRatings)
        } catch (err) {
            setError((err as Error).message ? (err as Error).message : "Failed to flag comment as inappropriate");
        }
        setIsLoading(false)
    };

    if (isLoading) {
        return (
            <Box sx={{ margin: 2 }}>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} sx={{ color: "black" }} />
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={{ margin: 2 }}>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Rating
                    value={rating}
                    precision={0.1}
                    readOnly
                    sx={{ color: '#162C1F', mr: 1 }}
                />
                <Typography variant="body2" color="textSecondary">
                    ({numRatings} reviews)
                </Typography>
            </Box>

            <CommentsSection
                comments={comments}
                onSubmitComment={data => addCommentCall(data)}
                onFlagInappropriate={flagCommentAsInappropriate}
                hideRatingComment={true}
            />

            {/* Error Modal */}
            <Dialog open={!!error} onClose={() => setError(null)}>
                <DialogTitle>
                    <AsyncString stringId="error" />
                </DialogTitle>
                <DialogContent>
                    <Typography>{error}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setError(null)} color="primary">
                        <AsyncString stringId="close" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};